/**
 * Question data service
 * Handles clinical question CRUD and workflow
 */

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Create a new question
 * @param {string} doctorId
 * @param {string} patientId
 * @param {string} text
 * @returns {string} Question ID
 */
export const createQuestion = async (doctorId, patientId, text) => {
  const docRef = await addDoc(collection(db, "questions"), {
    doctorId,
    patientId,
    text,
    status: "decomposing",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Get active question for patient
 * @param {string} patientId
 * @returns {Object}
 */
export const getActiveQuestion = async (patientId) => {
  const qRef = query(
    collection(db, "questions"),
    where("patientId", "==", patientId)
  );
  const snapshot = await getDocs(qRef);
  if (snapshot.empty) return null;

  const activeStatuses = new Set(["decomposing", "collecting", "summarizing"]);
  const active = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .find((qDoc) => activeStatuses.has(qDoc.status));

  return active || null;
};

/**
 * Get completed questions for patient
 * @param {string} patientId
 * @returns {Array}
 */
export const getCompletedQuestions = async (patientId) => {
  const qRef = query(
    collection(db, "questions"),
    where("patientId", "==", patientId),
    where("status", "==", "completed")
  );
  const snapshot = await getDocs(qRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Update question status
 * @param {string} questionId
 * @param {string} status
 */
export const updateQuestionStatus = async (questionId, status) => {
  await updateDoc(doc(db, "questions", questionId), { status });
};

/**
 * Get question by ID
 * @param {string} questionId
 * @returns {Object}
 */
export const getQuestion = async (questionId) => {
  const snap = await getDoc(doc(db, "questions", questionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const attachConversationToQuestion = async (questionId, convId) => {
  await updateDoc(doc(db, "questions", questionId), { convId });
};

export const subscribeActiveQuestion = (patientId, onData, onError) => {
  const qRef = query(
    collection(db, "questions"),
    where("patientId", "==", patientId)
  );

  return onSnapshot(
    qRef,
    (snapshot) => {
      if (snapshot.empty) {
        onData(null);
        return;
      }

      const activeStatuses = new Set(["decomposing", "collecting", "summarizing"]);
      const active = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .find((qDoc) => activeStatuses.has(qDoc.status));

      onData(active || null);
    },
    (error) => {
      if (onError) onError(error);
    }
  );
};

export const subscribeCompletedQuestions = (patientId, onData, onError) => {
  const qRef = query(
    collection(db, "questions"),
    where("patientId", "==", patientId),
    where("status", "==", "completed")
  );

  return onSnapshot(
    qRef,
    (snapshot) => {
      onData(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
};
